import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type OnboardingRole,
  HINTS,
  dismissHintsGlobally,
  isHintsGloballyDismissed,
  isOnboardingEnabled,
  loadDismissedHints,
  saveDismissedHints,
  trackOnboardingBreadcrumb,
} from '../lib/onboarding.js';

const HINT_DELAY_MS = 800;

export interface ContextHintsResult {
  /** Whether a specific hint should be visible right now */
  isVisible: (hintId: string) => boolean;
  /** Dismiss a single hint permanently */
  dismiss: (hintId: string) => void;
  /** Dismiss all hints globally */
  dismissAll: () => void;
  /** Mark a hint as seen (triggers delay, then show) */
  markSeen: (hintId: string) => void;
}

export function useContextHints(role: OnboardingRole): ContextHintsResult {
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissedHints());
  const [globallyOff, setGloballyOff] = useState(() => isHintsGloballyDismissed());
  const [readyHints, setReadyHints] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Available hints for this role
  const availableHintIds = useMemo(
    () =>
      new Set(
        HINTS
          .filter((h) => !h.coordinationOnly || role === 'coordination')
          .map((h) => h.id),
      ),
    [role],
  );

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const isVisible = useCallback(
    (hintId: string): boolean => {
      if (!isOnboardingEnabled() || globallyOff) return false;
      if (!availableHintIds.has(hintId)) return false;
      if (dismissed.has(hintId)) return false;
      return readyHints.has(hintId);
    },
    [globallyOff, availableHintIds, dismissed, readyHints],
  );

  const dismiss = useCallback(
    (hintId: string) => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(hintId);
        saveDismissedHints(next);
        return next;
      });
      setReadyHints((prev) => {
        const next = new Set(prev);
        next.delete(hintId);
        return next;
      });
      trackOnboardingBreadcrumb('hint_dismissed', { hintId, role });
    },
    [role],
  );

  const dismissAll = useCallback(() => {
    dismissHintsGlobally();
    setGloballyOff(true);
    setReadyHints(new Set());
    trackOnboardingBreadcrumb('hints_dismissed_globally', { role });
  }, [role]);

  const markSeen = useCallback(
    (hintId: string) => {
      if (!isOnboardingEnabled() || globallyOff) return;
      if (!availableHintIds.has(hintId)) return;
      if (dismissed.has(hintId)) return;
      if (readyHints.has(hintId)) return;
      if (timersRef.current.has(hintId)) return;

      const timer = setTimeout(() => {
        timersRef.current.delete(hintId);
        setReadyHints((prev) => {
          // Max 1 hint visible at a time — only show if no other is showing
          if (prev.size > 0) return prev;
          const next = new Set(prev);
          next.add(hintId);
          return next;
        });
      }, HINT_DELAY_MS);

      timersRef.current.set(hintId, timer);
    },
    [globallyOff, availableHintIds, dismissed, readyHints],
  );

  return { isVisible, dismiss, dismissAll, markSeen };
}
